"use client"

import { useSession } from "@/features/auth/context/session.context"
import { useNavigate, useLocation } from "react-router-dom"
import { useEffect, type PropsWithChildren, type ReactNode } from "react"
import { UserRole } from "@/shared/constants/user-roles"

// Role-based route access mapping (migrated from middleware.ts)
const routeAccessMap: Record<string, UserRole[]> = {
  "/partners/create": [UserRole.ADMIN, UserRole.BASE_LEADER],
  "/partners/edit": [UserRole.ADMIN, UserRole.BASE_LEADER],
  "/admin": [UserRole.ADMIN, UserRole.BASE_LEADER],
  "/migration": [UserRole.ADMIN, UserRole.BASE_LEADER],
  "/tools-supplies": [UserRole.ADMIN, UserRole.BASE_LEADER],
}

/** Auth routes that should NEVER be guarded */
const PUBLIC_ROUTES = new Set([
  "/",
  "/login",
  "/email-registration",
  "/forgot-password",
  "/verification",
  "/successfully-created",
  "/successfully-sent",
  "/reset-password",
  "/otp-login",
  "/role",
  "/supporter-registration",
  "/supporter-registration/register",
  "/supporter-registration/volunteer",
])

const REGION_AWARE_ROOTS = new Set([
  "home", "reports", "homeowners", "work-orders",
  "volunteers", "base", "migration", "partners", "admin",
])

function extractRouteFromPathname(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean)
  if (segments.length >= 2 && !REGION_AWARE_ROOTS.has(segments[0])) {
    return "/" + segments.slice(1).join("/")
  }
  return pathname
}

function hasAccessToRoute(userRole: UserRole, pathname: string): boolean {
  const route = extractRouteFromPathname(pathname)
  const requiredRoles = routeAccessMap[route]
  if (!requiredRoles) return true
  return requiredRoles.includes(userRole)
}

function isPublicRoute(pathname: string): boolean {
  if (PUBLIC_ROUTES.has(pathname)) return true
  if (pathname.startsWith("/auth/")) return true
  return false
}

interface AuthGuardProps extends PropsWithChildren {
  /** Optional custom loading skeleton. Defaults to a full-page centered spinner. */
  fallback?: ReactNode
}

/**
 * Client-side route protection component.
 *
 * Wraps protected layouts. Shows a skeleton while the session is being
 * restored via /auth/refresh, then redirects to login if no session exists.
 * Also enforces role-based access control.
 */
export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const { session, isLoading } = useSession()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    // Don't redirect while still loading
    if (isLoading) return

    // Public routes — let through
    if (isPublicRoute(pathname)) return

    // No session → redirect to login
    if (!session) {
      navigate("/", { replace: true })
      return
    }

    // Role-based access check
    if (!hasAccessToRoute(session.role as UserRole, pathname)) {
      navigate("/", { replace: true })
    }
  }, [isLoading, session, pathname, navigate])

  // Public routes render immediately
  if (isPublicRoute(pathname)) {
    return <>{children}</>
  }

  // Show skeleton while loading
  if (isLoading) {
    return fallback ? <>{fallback}</> : <AuthSkeleton />
  }

  // No session after load — show nothing (redirect is in-progress)
  if (!session) {
    return fallback ? <>{fallback}</> : <AuthSkeleton />
  }

  // Authorized
  return <>{children}</>
}

/**
 * Full-page loading skeleton displayed while auth state is being determined.
 */
function AuthSkeleton() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        width: "100%",
        background: "var(--background, #f8f9fa)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
        }}
      >
        {/* Animated spinner */}
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #e5e7eb",
            borderTopColor: "#3b82f6",
            borderRadius: "50%",
            animation: "auth-spin 0.8s linear infinite",
          }}
        />
        <style>{`
          @keyframes auth-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  )
}
